import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Mic, Search, X, Zap } from "lucide-react";
import { Button, InputGroup, Tabs, Tab } from "@heroui/react";
import DefaultLayout from "@/layouts/default-layout";
import TodaysMeetsComp from "@/components/meets/todays-meets-comp";
import UpComingMeetsComp from "@/components/meets/up-coming-meets-comp";
import PastMeetsComp from "@/components/meets/past-meets-comp";
import TabsComp from "@/components/tabs-comp";
import CreateQuickMeetsDrawer from "@/components/drawers/create-quick-meets-drawer";
import ErrorBoundary from "@/components/error-boundries/error-boundry-comp";

const MeetingsPage = () => {
  const { tab_id } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState("today");
  const [showQuickCreate, setShowQuickCreate] = useState(false);
  const [showSearchControls, setShowSearchControls] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [meetingCounts, setMeetingCounts] = useState({ today: 0, upcoming: 0, past: 0 });
  const numMeetings = meetingCounts[tab] ?? 0;
  const makeSetCount = (key) => (n) => setMeetingCounts((prev) => (prev[key] === n ? prev : { ...prev, [key]: n }));

  const tabs = [
    { key: "today", title: "Today" },
    { key: "upcoming", title: "Upcoming" },
    { key: "past", title: "Past" },
  ];

  useEffect(() => {
    if (tab_id && ["today", "upcoming", "past"].includes(tab_id)) {
      setTab(tab_id);
    }
  }, [tab_id]);

  const handleTabChange = (key) => {
    const tabKey = String(key);
    setTab(tabKey);
    navigate(`/${tabKey}`, { replace: false });
  };

  function toggleSearch() {
    setShowSearchControls((prev) => {
      if (prev) setSearchQuery("");
      return !prev;
    });
  }

  return (
    <>
      <div className="relative flex flex-col gap-5 mt-20">
        <div>
          {/* Copy */}
          <div className="flex flex-col gap-1 text-glass-foreground">
            <span className="uppercase text-xs font-bold tracking-[1.117px]">In the Loopp Today</span>

            <span className="text-[54px] font-bold">
              {numMeetings} Loopp{numMeetings > 1 || numMeetings === 0 ? "s" : ""}
            </span>

            <div className="flex flex-col gap-0 text-xl font-semibold">
              <span className="text-glass-foreground">Stay connected</span>
              <span className="text-glass-foreground-muted">Stay in the loop</span>
            </div>
          </div>
          {/* Nav */}
          <div className="w-full pointer-events-none mt-4">
            <div className="flex gap-2 justify-between items-center pointer-events-auto">
              <div className="w-full">
                <TabsComp items={tabs} selectedKey={tab} defaultKey="today" onChange={(key) => handleTabChange(key)} />
              </div>

              <div className="flex gap-2">
                <Button
                  onPress={toggleSearch}
                  aria-label={showSearchControls ? "Close search" : "Search meetings"}
                  size="md"
                  className={"rounded-full! icon-button h-[40px]! w-[40px]!"}
                  isIconOnly
                >
                  {showSearchControls ? <X size={16} className="size-4"/> : <Search className="h-4 w-4" />}
                </Button>
                <Button
                  onPress={() => {
                    setShowQuickCreate(true);
                  }}
                  aria-label="Log out"
                  size="md"
                  className={"rounded-full quick-meets-btn h-[40px] w-[40px]"}
                  isIconOnly
                >
                  <Zap className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {showSearchControls && (
              <div className="mt-3 pointer-events-auto">
                <InputGroup
                  fullWidth
                  className="bg-white/68 dark:bg-[#000000B2] rounded-2xl border border-black/[0.06] dark:border-white/10"
                >
                  <InputGroup.Input
                    autoFocus
                    placeholder="Search title, location, people, date..."
                    value={searchQuery}
                    className={"text-xs"}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <InputGroup.Suffix>
                    <button
                      type="button"
                      aria-label="Clear search"
                      onClick={toggleSearch}
                      className="text-muted hover:opacity-70 transition-opacity"
                    >
                      <X size={16} />
                    </button>
                  </InputGroup.Suffix>
                </InputGroup>
              </div>
            )}
          </div>
        </div>

        {/* Meeting Comps — only the active tab is mounted, so it re-fetches every time you navigate to it */}
        {tab === "today" && (
          <ErrorBoundary>
            <TodaysMeetsComp setNumMeetings={makeSetCount("today")} searchQuery={searchQuery} />
          </ErrorBoundary>
        )}

        {tab === "upcoming" && (
          <ErrorBoundary>
            <UpComingMeetsComp setNumMeetings={makeSetCount("upcoming")} searchQuery={searchQuery} />
          </ErrorBoundary>
        )}

        {tab === "past" && (
          <ErrorBoundary>
            <PastMeetsComp setNumMeetings={makeSetCount("past")} searchQuery={searchQuery} />
          </ErrorBoundary>
        )}
      </div>
      <CreateQuickMeetsDrawer _show={showQuickCreate} _setShow={setShowQuickCreate} />
    </>
  );
};

export default MeetingsPage;